import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { SubpageHero } from '../components/SubpageHero';
import { useCmsContent } from '../hooks/useCmsData';
import { SERVICES } from '../data/company';
import { getCdnImageUrl } from '../lib/utils';

export function ServicesPage() {
  const { items: cmsServices } = useCmsContent('ydelser', SERVICES);

  // Merge CMS items or fallback
  const servicesList = cmsServices && cmsServices.length > 0 ? cmsServices : SERVICES;

  return (
    <>
      <SEO 
        title="Ydelser" 
        description="Oversigt over ydelser fra Altmuligmanden Juelsminde: reparationer, montering, maling, havearbejde, flytning, rydning og generel vedligeholdelse." 
      />

      {/* Header Banner */}
      <SubpageHero
        badge="Professionel Håndværkshjælp"
        title="Vores Ydelser"
        subtitle="Hvad enten du skal have samlet møbler, ryddet loftet eller klippet hækken, står Altmuligmanden Juelsminde klar til at hjælpe."
        breadcrumbs={[
          { label: "Ydelser" }
        ]}
      />

      {/* Services Grid */}
      <section className="py-20 bg-sand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-12">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesList.map((service) => {
              const slug = service.service_slug || service.slug?._ || service.slug;
              const title = service.title;
              const shortDesc = service.short_description || service.shortDescription;
              const iconClass = service.icon || "fa-solid fa-wrench";
              const imgUrl = Array.isArray(service.main_image) ? service.main_image[0] : (service.main_image || service.image);

              return (
                <div
                  key={slug}
                  className="bg-white rounded-2xl overflow-hidden border border-sand-border shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group p-6 space-y-4"
                >
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-forest text-white rounded-xl flex items-center justify-center text-lg shadow-sm border border-white/20 shrink-0">
                        <i className={iconClass} aria-hidden="true" />
                      </div>
                      <h2 className="text-xl font-bold text-forest group-hover:text-copper transition-colors">
                        {title}
                      </h2>
                    </div>
                    <p className="text-muted text-sm leading-relaxed">
                      {shortDesc}
                    </p>
                  </div>

                  {imgUrl && (
                    <div className="relative aspect-16/10 bg-sand rounded-xl overflow-hidden border border-sand-border shadow-2xs">
                      <img
                        src={getCdnImageUrl(imgUrl, 800)}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}

                  <div className="pt-2">
                    <Link
                      to={`/ydelser/${slug}`}
                      className="inline-flex items-center gap-2 text-sm font-bold text-copper hover:text-copper-hover transition-colors cursor-pointer"
                    >
                      <span>Se detaljer om {title}</span>
                      <i className="fa-solid fa-arrow-right text-xs transform group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Special Custom Request Card */}
          <div className="bg-forest text-white rounded-3xl p-8 sm:p-12 shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-3">
              <h3 className="text-2xl sm:text-3xl font-bold">Har du en specifik eller anderledes opgave?</h3>
              <p className="text-sand/90 text-sm sm:text-base leading-relaxed">
                Finder du ikke præcis din opgave på listen ovenfor? Tøv ikke med at kontakte mig – vi finder næsten altid en god og gennemtænkt løsning til din bolig eller have.
              </p>
            </div>
            <div className="lg:col-span-4 flex justify-start lg:justify-end">
              <Link
                to="/kontakt"
                className="bg-copper hover:bg-copper-hover text-white font-bold px-8 py-4 rounded-xl shadow-md transition-all cursor-pointer whitespace-nowrap text-base"
              >
                <i className="fa-solid fa-comments mr-2" aria-hidden="true" />
                Kontakt for specialopgave
              </Link>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
